import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Sparkles, BookOpen, Search, Loader2, AlertCircle, CheckCircle2, ArrowLeft, Home } from 'lucide-react'
import { generateSlides, getAllTopics } from '../services/slideGenerator'
import { GeneratorInput, GeneratedSlide } from '../types/generator'
import { parseReference } from '../services/bibleApi'
import SlidePreview from '../components/SlidePreview'
import ParticleBackground from '../components/ParticleBackground'

const SlideGenerator: React.FC = () => {
  const [mode, setMode] = useState<'reference' | 'topic'>('reference')
  const [reference, setReference] = useState('')
  const [topic, setTopic] = useState('')
  const [availableTopics, setAvailableTopics] = useState<string[]>([])
  const [includeExplanations, setIncludeExplanations] = useState(true)
  const [includeApplications, setIncludeApplications] = useState(true)
  const [maxSlides, setMaxSlides] = useState(10)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [generatedSlides, setGeneratedSlides] = useState<GeneratedSlide[] | null>(null)
  const [showPreview, setShowPreview] = useState(false)
  const [previousChapterLink, setPreviousChapterLink] = useState<string | null>(null)

  useEffect(() => {
    // Load available topics
    getAllTopics().then(topics => setAvailableTopics(topics))
    
    // Check for URL parameters
    const params = new URLSearchParams(window.location.search)
    const referenceParam = params.get('reference')
    const topicParam = params.get('topic')
    
    if (referenceParam) {
      setMode('reference')
      const decodedRef = decodeURIComponent(referenceParam)
      setReference(decodedRef)
      
      // Parse reference to create link back to chapter
      const parsed = parseReference(decodedRef)
      if (parsed) {
        setPreviousChapterLink(`/book/${parsed.bookId}/${parsed.chapter}`)
      }
    } else if (topicParam) {
      setMode('topic')
      setTopic(decodeURIComponent(topicParam))
    }
  }, [])

  const handleGenerate = async () => {
    setError(null)
    setLoading(true)

    try {
      const input: GeneratorInput = {
        mode,
        reference: mode === 'reference' ? reference : undefined,
        topic: mode === 'topic' ? topic : undefined,
        includeExplanations,
        includeApplications,
        maxSlides,
      }

      const slides = await generateSlides(input)
      setGeneratedSlides(slides)
      setShowPreview(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate slides')
    } finally {
      setLoading(false)
    }
  }

  const handleStartPresentation = (slides: GeneratedSlide[]) => {
    // Store slides in sessionStorage and navigate to presentation
    sessionStorage.setItem('generatedSlides', JSON.stringify(slides))
    window.location.href = '/presentation'
  }

  const isValid = mode === 'reference' 
    ? reference.trim().length > 0
    : topic.trim().length > 0

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative">
      <ParticleBackground />
      
      <div className="relative z-10 container mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          {/* Navigation Links */}
          <div className="mb-6 flex items-center gap-4 flex-wrap">
            <a
              href="/"
              className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 hover:bg-slate-700 text-bible-gold rounded-lg transition-colors text-sm font-medium border border-slate-700"
            >
              <Home size={18} />
              <span>All Books</span>
            </a>
            {previousChapterLink && (
              <a
                href={previousChapterLink}
                className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 hover:bg-slate-700 text-bible-gold rounded-lg transition-colors text-sm font-medium border border-slate-700"
              >
                <ArrowLeft size={18} />
                <span>Previous Chapter</span>
              </a>
            )}
          </div>

          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Sparkles className="text-bible-gold" size={48} />
              <h1 className="text-5xl font-bold text-bible-gold">Slideshow Generator</h1>
            </div>
            <p className="text-xl text-gray-300">
              Generate beautiful Bible presentations automatically
            </p>
          </div>

          {/* Mode Selection */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 mb-8 border border-slate-700"
          >
            <div className="flex gap-4 mb-6">
              <button
                onClick={() => setMode('reference')}
                className={`flex-1 px-6 py-4 rounded-lg font-semibold transition-all ${
                  mode === 'reference'
                    ? 'bg-bible-gold text-slate-900 shadow-lg'
                    : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                }`}
              >
                <BookOpen className="inline-block mr-2" size={20} />
                Bible Reference
              </button>
              <button
                onClick={() => setMode('topic')}
                className={`flex-1 px-6 py-4 rounded-lg font-semibold transition-all ${
                  mode === 'topic'
                    ? 'bg-bible-gold text-slate-900 shadow-lg'
                    : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                }`}
              >
                <Search className="inline-block mr-2" size={20} />
                Topic/Theme
              </button>
            </div>

            {/* Input Fields */}
            {mode === 'reference' ? (
              <div>
                <label className="block text-gray-300 mb-2 font-medium">
                  Bible Reference
                </label>
                <input
                  type="text"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder="e.g., Genesis 1:1-5, John 3:16, Romans 8:28-30"
                  className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg border border-slate-600 focus:border-bible-gold focus:outline-none focus:ring-2 focus:ring-bible-gold/50"
                />
                <p className="mt-2 text-sm text-gray-400">
                  Enter a Bible reference in the format: Book Chapter:Verse or Book Chapter:StartVerse-EndVerse
                </p>
              </div>
            ) : (
              <div>
                <label className="block text-gray-300 mb-2 font-medium">
                  Topic or Theme
                </label>
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="e.g., love, faith, creation, salvation"
                  className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg border border-slate-600 focus:border-bible-gold focus:outline-none focus:ring-2 focus:ring-bible-gold/50"
                />
                <p className="mt-2 text-sm text-gray-400">
                  Available topics: {availableTopics.join(', ')}
                </p>
              </div>
            )}
          </motion.div>

          {/* Options */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 mb-8 border border-slate-700"
          >
            <h3 className="text-xl font-semibold text-bible-gold mb-4">Options</h3>
            
            <div className="space-y-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeExplanations}
                  onChange={(e) => setIncludeExplanations(e.target.checked)}
                  className="w-5 h-5 text-bible-gold rounded focus:ring-bible-gold"
                />
                <span className="text-gray-300">Include explanation slides</span>
              </label>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeApplications}
                  onChange={(e) => setIncludeApplications(e.target.checked)}
                  className="w-5 h-5 text-bible-gold rounded focus:ring-bible-gold"
                />
                <span className="text-gray-300">Include application slides</span>
              </label>

              <div>
                <label className="block text-gray-300 mb-2">
                  Maximum Slides: {maxSlides}
                </label>
                <input
                  type="range"
                  min="5"
                  max="20"
                  value={maxSlides}
                  onChange={(e) => setMaxSlides(parseInt(e.target.value, 10))}
                  className="w-full"
                />
              </div>
            </div>
          </motion.div>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-900/50 border border-red-700 rounded-lg p-4 mb-6 flex items-center gap-3"
            >
              <AlertCircle className="text-red-400" size={24} />
              <p className="text-red-200">{error}</p>
            </motion.div>
          )}

          {/* Generate Button */}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            onClick={handleGenerate}
            disabled={!isValid || loading}
            className="w-full px-8 py-4 bg-bible-gold text-slate-900 rounded-lg font-bold text-lg hover:bg-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg flex items-center justify-center gap-3"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={24} />
                Generating Slides...
              </>
            ) : (
              <>
                <Sparkles size={24} />
                Generate Presentation
              </>
            )}
          </motion.button>

          {/* Success Message */}
          {generatedSlides && !showPreview && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-green-900/50 border border-green-700 rounded-lg p-4 mt-6 flex items-center gap-3"
            >
              <CheckCircle2 className="text-green-400" size={24} />
              <p className="text-green-200">
                Successfully generated {generatedSlides.length} slides!
              </p>
            </motion.div>
          )}
        </motion.div>
      </div>

      {/* Slide Preview Modal */}
      {showPreview && generatedSlides && (
        <SlidePreview
          slides={generatedSlides}
          onClose={() => setShowPreview(false)}
          onStartPresentation={handleStartPresentation}
        />
      )}
    </div>
  )
}

export default SlideGenerator

